import { useMemo } from "react";
import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { RoleS, normalizeRole } from "@ugg/shared/utils/role-helpers";

// ─────────────────────────────────────────────────────────────────────
// League Classic shared data layer
// ─────────────────────────────────────────────────────────────────────
//
// Types, cleaners, image-URL helpers, and JSONFetcher hooks for the
// League Classic custom data pipeline. Modeled on the ARAM Mayhem data
// layer (see hooks/aram-mayhem.ts): data-only, no UI, so both the
// tier-list surface and any future Classic consumers (champion pages,
// SEO blocks, etc.) can share the same fetch layer + cache slots.
//
// Classic champions are NOT part of the standard riot-assets pipeline,
// so their portraits, ability icons, and champion JSON live under a
// dedicated CDN base rather than resolving through @outplayed/riot-assets.
// The helpers below build those URLs; only the champion manifest is
// actually consumed by the tier list today, but the rest are provided so
// other features can hang off the same base without re-deriving it.

// Root of the League Classic custom cdragon pipeline. Every sub-manifest
// (champions, mastery, runes, spells, items) hangs off this base in its own
// folder. Kept separate from the standard riot-assets pipeline because none
// of this data is available through DDragon today — see the file header.
//
// NOTE: we intentionally use the "league_classic" slug rather than a patch
// number (e.g. "16_14") for the initial data setup. This will move to a
// patch-versioned path once the pipeline stabilizes, at which point only
// these base constants need to change.
export const CLASSIC_CDRAGON_BASE_URL = "https://static.bigbrain.gg/cdragon-custom/league_classic";

// Base CDN url for the League Classic champion assets. Everything for a
// champion is keyed off its numeric champion key:
//   ${BASE}/classic-champions.json  — id → [alias, displayName] manifest
//   ${BASE}/${key}.webp             — champion portrait icon
//   ${BASE}/${key}.json             — champion info (spells, stats, ...)
//   ${BASE}/${key}_{q|w|e|r|p}.webp — ability icons (p = passive)
export const CLASSIC_CHAMPION_BASE_URL = `${CLASSIC_CDRAGON_BASE_URL}/classic-champions`;

// Base CDN urls for the remaining Classic manifests. Each folder holds a
// single JSON manifest plus id/name-keyed webp icons (see per-hook docs).
export const CLASSIC_MASTERY_BASE_URL = `${CLASSIC_CDRAGON_BASE_URL}/mastery`;
export const CLASSIC_RUNES_BASE_URL = `${CLASSIC_CDRAGON_BASE_URL}/runes`;
export const CLASSIC_SPELLS_BASE_URL = `${CLASSIC_CDRAGON_BASE_URL}/spells`;
export const CLASSIC_ITEMS_BASE_URL = `${CLASSIC_CDRAGON_BASE_URL}/items`;

// ─────────────────────────────────────────────────────────────────────
// JSON payload interfaces
// ─────────────────────────────────────────────────────────────────────

// classic-champions.json — flat manifest keyed by numeric champion key
// (as a string). Each value is a positional tuple, NOT an object:
//   [0] normalized id / alias ("annie")
//   [1] display name          ("Annie")
// Typed as a fixed-length tuple so [0]/[1] access is checked.
export type ClassicChampionEntry = [alias: string, displayName: string];
export type ClassicChampionsManifest = Record<string, ClassicChampionEntry>;

// Ability icon keys available per champion — q/w/e/r spells + p passive.
export type ClassicSkillKey = "q" | "w" | "e" | "r" | "p";

// Base CDN url for the Classic tier-list role icons. Separate from the
// champion asset base above. Files are keyed by the LOWERCASE role letter
// (t/j/m/b/s), while the tier-list JSON carries them uppercase — the
// helper below normalizes, so callers can pass either case.
export const CLASSIC_TIER_LIST_ROLE_BASE_URL = "https://static.bigbrain.gg/custom-league-classic/assets/tier-list-role";

// Full role names for the role-letter shorthand used in the tier-list
// JSON. Keyed on the lowercase letter; used for icon alt/title text.
// Capitalized to match the site's role labels ("Middle", not "Mid").
export const CLASSIC_ROLE_NAMES: Record<string, string> = {
  t: "Top",
  j: "Jungle",
  m: "Middle",
  b: "Bot",
  s: "Support",
};

export function getClassicRoleIconUrl(role: string) {
  return `${CLASSIC_TIER_LIST_ROLE_BASE_URL}/${String(role ?? "").toLowerCase()}.webp`;
}

export function getClassicRoleName(role: string) {
  return CLASSIC_ROLE_NAMES[String(role ?? "").toLowerCase()] ?? role;
}

// Maps a site role (RoleS) to the role letter used in the tier-list JSON.
// Keyed on RoleS so it goes through normalizeRole below, which collapses
// the SEO "adc" value plus "bot"/"bottom" all onto RoleS.BOTTOM ("B") —
// that's how the filter supports bot/adc interchangeably.
const CLASSIC_ROLE_LETTER_BY_ROLE: Partial<Record<RoleS, string>> = {
  [RoleS.TOP]: "T",
  [RoleS.JUNGLE]: "J",
  [RoleS.MIDDLE]: "M",
  [RoleS.BOTTOM]: "B",
  [RoleS.SUPPORT]: "S",
};

// Resolves a role query value (top/jungle/mid/adc|bot|bottom/support, or
// the RoleS equivalents) to its tier-list role letter. Returns undefined
// for "all"/"none"/unknown — i.e. "don't filter by role".
export function getClassicRoleLetter(role: string | undefined | null): string | undefined {
  if (!role) return undefined;
  return CLASSIC_ROLE_LETTER_BY_ROLE[normalizeRole(role) as RoleS];
}

// Inverse of getClassicRoleLetter: a tier-list role letter (T/J/M/B/S, either
// case) → the site RoleS slug (top/jungle/mid/adc/support). Undefined for
// unknown letters. Used by the champion page to turn a tier entry's role into
// the slug the role selector + build URL expect.
export function getClassicRoleFromLetter(letter: string | undefined | null): RoleS | undefined {
  if (!letter) return undefined;
  const upper = letter.toUpperCase();
  return (Object.keys(CLASSIC_ROLE_LETTER_BY_ROLE) as RoleS[]).find((role) => CLASSIC_ROLE_LETTER_BY_ROLE[role] === upper);
}

// Champion tier list JSON. Same envelope as the ARAM Mayhem champion
// tier list, but each tier entry is a [championKey, roleLetter] tuple
// rather than a bare champion key — Classic carries a role association
// (T/J/M/B/S) that ARAM Mayhem did not.
export type ClassicTierEntry = [championKey: number, role: string];
export interface ClassicChampionTierList {
  // Partial: the payload only carries tiers that have champions, so an absent
  // tier is `undefined` (not `[]`). Indexing therefore forces callers to guard
  // — matching reality and preventing a `tiers["s-tier"].map(...)` crash.
  tiers: Partial<Record<TierNameValue, ClassicTierEntry[]>>;
  lastUpdated: string;
}

// ─────────────────────────────────────────────────────────────────────
// Image / asset URL helpers — plain string builders (no hooks) so they
// can be used anywhere, including outside React.
// ─────────────────────────────────────────────────────────────────────

export function getClassicChampionImgUrl(championKey: number | string) {
  return `${CLASSIC_CHAMPION_BASE_URL}/${championKey}.webp`;
}

export function getClassicChampionSkillImgUrl(championKey: number | string, skill: ClassicSkillKey) {
  return `${CLASSIC_CHAMPION_BASE_URL}/${championKey}_${skill}.webp`;
}

export function getClassicChampionJSONUrl(championKey: number | string) {
  return `${CLASSIC_CHAMPION_BASE_URL}/${championKey}.json`;
}

// ─────────────────────────────────────────────────────────────────────
// Cleaners — normalize the raw fetch payload, seeding empty defaults for
// the loading state so consumers can dereference without null-checks.
// ─────────────────────────────────────────────────────────────────────

// The manifest is a flat id→tuple dict (no envelope), so the cleaner just
// guards against the null/undefined the fetcher passes during loading.
export function cleanClassicChampions(data: ClassicChampionsManifest | null | undefined) {
  return data ?? ({} as ClassicChampionsManifest);
}

export function cleanClassicChampionTierList(data: ClassicChampionTierList | null | undefined) {
  const tierlist: Partial<Record<TierNameValue, ClassicTierEntry[]>> = data?.tiers ?? {};
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

// ─────────────────────────────────────────────────────────────────────
// Fetch hooks — thin JSONFetcher wrappers around the CDN URLs.
// ─────────────────────────────────────────────────────────────────────

// Champion metadata manifest (id → [alias, displayName]). Patch-
// independent, so there is no patch gating — the URL is stable. Pass
// `ssr: true` to prefetch the names during server render so they land in
// the client's JSONFetcher cache on hydration (SSR handling mirrors the
// rest of the JSONFetcher hooks).
export function useClassicChampions<K = ReturnType<typeof cleanClassicChampions>>(
  options?: JSONFetcherOptions<ClassicChampionsManifest, K>,
) {
  const { ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const url = `${CLASSIC_CHAMPION_BASE_URL}/classic-champions.json`;
  return useJSONFetcher<ClassicChampionsManifest, K>(url, {
    skip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicChampions(json) as K),
    customCacheKey,
  });
}

// Per-champion info JSON (spells, stats, tactical info, ...). Not used by
// the tier list, but provided so downstream features (a Classic champion
// page, tooltips, etc.) can fetch a champion's full payload off the same
// base. Skips the fetch when championKey is missing/0 to avoid a
// malformed `.../undefined.json` request.
//
// Typed on the PAYLOAD generic (T) with no `onCompleted` transform, so the
// returned `data` is `T | undefined` — undefined during load and on a 404.
// (Threading the fetcher's second generic K instead would collapse the type
// to a non-optional `K`, hiding the loading/missing case and inviting an
// unguarded `champion.spells` access that type-checks but crashes at runtime.)
export function useClassicChampionJSON<T = ClassicChampionData>(
  options: { championKey: number | string } & JSONFetcherOptions<T>,
) {
  const { championKey, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !championKey;

  const url = getClassicChampionJSONUrl(championKey);
  return useJSONFetcher<T>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Per-champion info JSON (${key}.json) payload shape
// ─────────────────────────────────────────────────────────────────────
//
// The champion JSON carries everything the profile page needs beyond the
// portrait: the playstyle ratings, tag/role labels, the shortBio quick
// facts, and the full spell list (with a hand-authored dynamicDescription
// used to build the on-hover skill tooltips). Fields we don't consume yet
// (effectAmounts, ammo, coefficients) are typed loosely on purpose.

export interface ClassicChampionSpellData {
  spellKey: "q" | "w" | "e" | "r";
  name: string;
  cost: string;
  cooldown: string;
  description: string;
  // Hand-authored tooltip body. Contains custom color tags (<magicDamage>…),
  // <br> breaks, and @Placeholder@ tokens; unresolved tokens render as "?".
  dynamicDescription: string;
  range: number[];
  costCoefficients: number[];
  cooldownCoefficients: number[];
  coefficients: { coefficient1: number; coefficient2: number };
  effectAmounts: Record<string, number[]>;
  ammo: { ammoRechargeTime: number[]; maxAmmo: number[] };
  maxLevel: number;
}

export interface ClassicChampionData {
  id: number;
  relatedPrimeItemId: number;
  name: string;
  alias: string;
  title: string;
  // HTML string of "- fact<br>- fact" quick notes shown in Champion Info.
  shortBio: string;
  isVisibleInClient: boolean;
  tacticalInfo: { style: number; difficulty: number; damageType: string; attackType: string };
  // 0–3 ratings driving the Champion Info bars.
  playstyleInfo: { damage: number; durability: number; crowdControl: number; mobility: number; utility: number };
  championTagInfo: { championTagPrimary: string; championTagSecondary: string };
  roles: string[];
  recommendedItemDefaults: unknown[];
  passive: { name: string; description: string };
  spells: ClassicChampionSpellData[];
}

// ─────────────────────────────────────────────────────────────────────
// Curated per-champion skill order (priority + leveling path).
// ─────────────────────────────────────────────────────────────────────
//
// Unlike the rest of the Classic pipeline (which lives under cdragon-custom
// and is patch-independent for now), the curated skill data lives in the
// PATCH-VERSIONED custom bucket alongside the tier list:
//   https://static.bigbrain.gg/custom-league-classic/<patch>/skills/<championId>.json
// Each file has a championId plus 1–5 role objects (top/jungle/mid/bot/support).
// `priority` is the max-order (e.g. ["Q","W","E"]); `path` is one ability key
// per level (index 0 = level 1). NOTE the JSON uses "bot", which maps to the
// site's "adc" role. Only a few champions have data at launch, so most fetches
// 404 — callers treat that as "no curated data yet".
export const CLASSIC_CUSTOM_LEAGUE_BASE_URL = "https://static.bigbrain.gg/custom-league-classic";

export interface ClassicSkillRoleData {
  priority: string[];
  path: string[];
}
export interface ClassicChampionSkills {
  championId: number;
  lastUpdated?: string;
  top?: ClassicSkillRoleData;
  jungle?: ClassicSkillRoleData;
  mid?: ClassicSkillRoleData;
  bot?: ClassicSkillRoleData;
  support?: ClassicSkillRoleData;
}

// Skips the fetch when championKey or patch is missing (avoids a malformed URL
// during the loading window before patches resolve).
export function useClassicChampionSkills<K = ClassicChampionSkills>(
  options: { championKey: number | string; patch: string | undefined } & JSONFetcherOptions<ClassicChampionSkills, K>,
) {
  const { championKey, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !championKey || !patch;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/skills/${championKey}.json`;
  return useJSONFetcher<ClassicChampionSkills, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ── Mini skill-priority feed (compact per-champion format) ──
// The full skills file above carries priority + the full leveling path per role,
// but the UI now only surfaces the 3-ability priority order. So a lighter file
// holds just that: a flat array of ability keys, e.g. ["Q","W","E"]. Keyed by
// champion key in a `mini/` subfolder of the same patch-versioned skills dir:
//   https://static.bigbrain.gg/custom-league-classic/<patch>/skills/mini/<championKey>.json
// Not every champion has one yet, so most fetches 404 → data undefined, which
// the UI renders as blank priority slots. The full useClassicChampionSkills
// fetcher above is kept intact for when we surface the leveling path again.
export type ClassicChampionSkillsMini = string[];

// Skips the fetch when championKey or patch is missing (avoids a malformed URL
// during the loading window before patches resolve).
export function useClassicChampionSkillsMini<K = ClassicChampionSkillsMini>(
  options: { championKey: number | string; patch: string | undefined } & JSONFetcherOptions<ClassicChampionSkillsMini, K>,
) {
  const { championKey, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !championKey || !patch;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/skills/mini/${championKey}.json`;
  return useJSONFetcher<ClassicChampionSkillsMini, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Mastery manifest (jade-mastery-display.json) — the three trees rendered
// by the Masteries module, plus id/on-off icons keyed by mastery id.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicMasteryNode {
  id: number;
  name: string;
  rowIndex: number;
  maxRank: number;
  description: string;
  requiresMastery?: number;
}
// A row holds a fixed set of columns; `null` marks an empty cell so the grid
// keeps its column alignment.
export interface ClassicMasteryRow {
  pointsRequired: number;
  masteries: Array<ClassicMasteryNode | null>;
}
export interface ClassicMasteryTree {
  name: string;
  type: string; // "offense" | "defense" | "utility"
  rows: ClassicMasteryRow[];
}
export interface ClassicMasteryDisplay {
  trees: ClassicMasteryTree[];
}

// The selected-mastery map (mastery id → points allocated) is what both the
// Masteries module (on/off icons + rank badges) and the Mastery Summary
// (tooltips) read. One curated tree per champion — see useClassicChampionMastery.

export function getClassicMasteryIconUrl(masteryId: number | string, active: boolean) {
  return `${CLASSIC_MASTERY_BASE_URL}/${masteryId}_${active ? "on" : "off"}.webp`;
}

// Per-tree background art for the Masteries panels (already tinted red/blue/green
// in the source). Keyed by tree type: offense/defense/utility.
export const CLASSIC_MASTERY_TREE_BG_BASE_URL = "https://static.bigbrain.gg/custom-league-classic/assets/mastery";
export function getClassicMasteryTreeBgUrl(type: string) {
  return `${CLASSIC_MASTERY_TREE_BG_BASE_URL}/${type.toLowerCase()}.webp`;
}

// ─── Curated mastery tree (one file per champion) ────────────────────
//
// Distinct from the mastery DISPLAY tree above (which defines the grid): each
// champion has a hand-authored point allocation in the PATCH-VERSIONED custom
// bucket (like skills), one file keyed by champion key:
//   https://static.bigbrain.gg/custom-league-classic/<patch>/mastery/<championKey>.json
// The file groups id/rank pairs by category; the module only needs the selected
// ids, so we flatten to a { id: rank } map and treat any rank > 0 as "on". Not
// every champion has a file yet — a missing one (404) yields an empty selection,
// so the tree renders with no allocations (there is no fallback build).
export interface ClassicMasteryBuildFile {
  masteries: {
    offense: Array<[number, number]>;
    defense: Array<[number, number]>;
    utility: Array<[number, number]>;
  };
  lastUpdated?: string;
}

// Flatten the categorized id/rank pairs into the { id: rank } map the modules
// consume, keeping only allocated (rank > 0) nodes.
export function flattenClassicMasteryBuild(file: ClassicMasteryBuildFile | null | undefined): Record<number, number> {
  const selected: Record<number, number> = {};
  if (!file?.masteries) return selected;
  (["offense", "defense", "utility"] as const).forEach((category) => {
    const entries = file.masteries[category];
    if (!Array.isArray(entries)) return;
    entries.forEach((entry) => {
      if (!Array.isArray(entry)) return;
      const [id, rank] = entry;
      if (rank > 0) selected[id] = rank;
    });
  });
  return selected;
}

// Fetch a champion's curated mastery tree and flatten it to the { id: rank }
// map the modules consume. Skips on missing patch/championKey (avoids a bad
// URL). A missing file (404) leaves `data` undefined → an empty selection, so
// the tree simply renders with nothing allocated (no fallback build).
export function useClassicChampionMastery(options: {
  championKey: string | number | undefined;
  patch: string | undefined;
  ssr?: boolean;
}): { selected: Record<number, number>; loading: boolean } {
  const { championKey, patch, ssr = false } = options;
  const effectiveSkip = !patch || !championKey;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/mastery/${championKey}.json`;
  const { data, loading } = useJSONFetcher<ClassicMasteryBuildFile>(url, { skip: effectiveSkip, ssr });

  const selected = useMemo(() => flattenClassicMasteryBuild(data), [data]);
  return { selected, loading };
}

// ─────────────────────────────────────────────────────────────────────
// Rune (perk) manifest (jade-perks.json). Icons are keyed by iconPath
// (e.g. "b_3_3.webp") rather than id because the art is shared across perks.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicPerk {
  id: number;
  type: string; // kMark | kSeal | kGlyph | kQuintessence
  statName: string;
  tooltip: string;
  title: string;
  amount: number;
  isPerLevel: boolean;
  isLowQuality: boolean;
  totalAmount?: number;
  stats: Record<string, number>;
  itemId: number;
  iconPath?: string;
  primaryStatGroup: string;
}

export function getClassicRuneIconUrl(iconPath: string) {
  return `${CLASSIC_RUNES_BASE_URL}/${iconPath}`;
}

// Curated rune build — the selected perks grouped by rune type, each entry a
// [perkId, count] pair (resolve names/effects/icons against the jade-perks.json
// manifest above). Like the skill/mastery/spell feeds, it lives in the
// PATCH-VERSIONED custom bucket:
//   https://static.bigbrain.gg/custom-league-classic/<patch>/runes/<build>.json
// Champion/role → build routing is a later step; for now the caller points at a
// single hardcoded file name.
export interface ClassicRuneBuildFile {
  runes: {
    kQuintessence?: Array<[number, number]>;
    kMark?: Array<[number, number]>;
    kSeal?: Array<[number, number]>;
    kGlyph?: Array<[number, number]>;
  };
  lastUpdated?: string;
}

export function useClassicRuneBuild<K = ClassicRuneBuildFile>(
  options: { name: string; patch: string | undefined } & JSONFetcherOptions<ClassicRuneBuildFile, K>,
) {
  const { name, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !patch || !name;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/runes/${encodeURIComponent(name)}.json`;
  return useJSONFetcher<ClassicRuneBuildFile, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Summoner spell manifest (classic-spells.json). Icons keyed by spell id.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicSummonerSpell {
  id: number;
  name: string;
  description: string;
  cooldown: number;
}

export function getClassicSpellIconUrl(spellId: number | string) {
  return `${CLASSIC_SPELLS_BASE_URL}/${spellId}.webp`;
}

// Curated summoner-spell build — which two spells to recommend. Like the skill
// and mastery build feeds, it lives in the PATCH-VERSIONED custom bucket, one
// file per build named after the pairing:
//   https://static.bigbrain.gg/custom-league-classic/<patch>/spells/<build>.json
// The file is just the ordered spell ids (resolve names/tooltips against the
// classic-spells.json manifest above). Champion/role → build routing is a
// later step; for now the caller points at a single hardcoded file name.
export interface ClassicSpellBuildFile {
  spells: number[];
  lastUpdated?: string;
}

export function useClassicSpellBuild<K = ClassicSpellBuildFile>(
  options: { name: string; patch: string | undefined } & JSONFetcherOptions<ClassicSpellBuildFile, K>,
) {
  const { name, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !patch || !name;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/spells/${encodeURIComponent(name)}.json`;
  return useJSONFetcher<ClassicSpellBuildFile, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Item manifests. Two files: the full catalog (classic-items.json, ~110kb,
// carries tooltip HTML + recipe trees) and a lightweight id→name lookup
// (classic-items-mini.json) so icons/names render before the big file
// lands. Icons keyed by item id.
// ─────────────────────────────────────────────────────────────────────

export interface ClassicItem {
  id: number;
  name: string;
  description: string;
  active: boolean;
  inStore: boolean;
  from: number[];
  to: number[];
  categories: string[];
  maxStacks: number;
  requiredChampion: string;
  requiredAlly: string;
  requiredBuffCurrencyName: string;
  requiredBuffCurrencyCost: number;
  specialRecipe: number;
  isEnchantment: boolean;
  price: number;
  priceTotal: number;
  displayInItemSets: boolean;
  priceCalculated: number;
  inShopList: boolean;
}
export type ClassicItemsMini = Record<string, string>;

export function getClassicItemIconUrl(itemId: number | string) {
  return `${CLASSIC_ITEMS_BASE_URL}/${itemId}.webp`;
}

// Curated item build. Like the skill/mastery/spell/rune feeds, it lives in the
// PATCH-VERSIONED custom bucket:
//   https://static.bigbrain.gg/custom-league-classic/<patch>/items/<build>.json
// `starter` is the opening buy (duplicate ids = a stacked consumable, e.g. a
// potion x2); `endgame` is the finished build (≤ 6 items, enforced upstream).
// Resolve names/tooltips/icons against the mini + full item manifests above.
export interface ClassicItemBuildFile {
  items: {
    starter: number[];
    endgame: number[];
  };
  lastUpdated?: string;
}

export function useClassicItemBuild<K = ClassicItemBuildFile>(
  options: { name: string; patch: string | undefined } & JSONFetcherOptions<ClassicItemBuildFile, K>,
) {
  const { name, patch, ssr = false, skip = false, onCompleted, customCacheKey } = options;
  const effectiveSkip = skip || !patch || !name;

  const url = `${CLASSIC_CUSTOM_LEAGUE_BASE_URL}/${patch}/items/${encodeURIComponent(name)}.json`;
  return useJSONFetcher<ClassicItemBuildFile, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted,
    customCacheKey,
  });
}

// ─────────────────────────────────────────────────────────────────────
// Cleaners for the new manifests. Each seeds an empty default so consumers
// can dereference during loading without null-checks.
// ─────────────────────────────────────────────────────────────────────

// Array.isArray (not just `?? []`): if a payload arrives wrong-typed (an object
// where an array is expected), return empty so the type stays honest and no
// downstream `.map`/`.forEach` can throw — empty beats a crash.
export function cleanClassicMasteryDisplay(data: ClassicMasteryDisplay | null | undefined) {
  return { trees: Array.isArray(data?.trees) ? data!.trees : [] } as ClassicMasteryDisplay;
}
export function cleanClassicPerks(data: ClassicPerk[] | null | undefined) {
  return Array.isArray(data) ? data : [];
}
export function cleanClassicSpells(data: ClassicSummonerSpell[] | null | undefined) {
  return Array.isArray(data) ? data : [];
}
export function cleanClassicItems(data: ClassicItem[] | null | undefined) {
  return Array.isArray(data) ? data : [];
}
export function cleanClassicItemsMini(data: ClassicItemsMini | null | undefined) {
  return data ?? ({} as ClassicItemsMini);
}

// ─────────────────────────────────────────────────────────────────────
// Fetch hooks for the new manifests. All are patch-independent for now
// (they live under the "league_classic" slug — see CLASSIC_CDRAGON_BASE_URL),
// so there is no patch gating; the URLs are stable. `ssr: true` prefetches
// during server render for SEO, mirroring useClassicChampions.
// ─────────────────────────────────────────────────────────────────────

export function useClassicMasteryDisplay<K = ReturnType<typeof cleanClassicMasteryDisplay>>(
  options?: JSONFetcherOptions<ClassicMasteryDisplay, K>,
) {
  const { ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const url = `${CLASSIC_MASTERY_BASE_URL}/jade-mastery-display.json`;
  return useJSONFetcher<ClassicMasteryDisplay, K>(url, {
    skip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicMasteryDisplay(json) as K),
    customCacheKey,
  });
}

export function useClassicPerks<K = ReturnType<typeof cleanClassicPerks>>(options?: JSONFetcherOptions<ClassicPerk[], K>) {
  const { ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const url = `${CLASSIC_RUNES_BASE_URL}/jade-perks.json`;
  return useJSONFetcher<ClassicPerk[], K>(url, {
    skip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicPerks(json) as K),
    customCacheKey,
  });
}

export function useClassicSpells<K = ReturnType<typeof cleanClassicSpells>>(
  options?: JSONFetcherOptions<ClassicSummonerSpell[], K>,
) {
  const { ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const url = `${CLASSIC_SPELLS_BASE_URL}/classic-spells.json`;
  return useJSONFetcher<ClassicSummonerSpell[], K>(url, {
    skip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicSpells(json) as K),
    customCacheKey,
  });
}

export function useClassicItems<K = ReturnType<typeof cleanClassicItems>>(options?: JSONFetcherOptions<ClassicItem[], K>) {
  const { ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const url = `${CLASSIC_ITEMS_BASE_URL}/classic-items.json`;
  return useJSONFetcher<ClassicItem[], K>(url, {
    skip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicItems(json) as K),
    customCacheKey,
  });
}

// Lightweight id→name lookup. Fetch this alongside the icons for fast
// first paint; the full useClassicItems payload (tooltips/recipes) can
// resolve afterwards without blocking the initial render.
export function useClassicItemsMini<K = ReturnType<typeof cleanClassicItemsMini>>(
  options?: JSONFetcherOptions<ClassicItemsMini, K>,
) {
  const { ssr = false, skip = false, onCompleted, customCacheKey } = options || {};
  const url = `${CLASSIC_ITEMS_BASE_URL}/classic-items-mini.json`;
  return useJSONFetcher<ClassicItemsMini, K>(url, {
    skip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicItemsMini(json) as K),
    customCacheKey,
  });
}

// Champion tier list per patch. Not wired into the container yet — the
// Classic tier-list data pipeline isn't producing this file on a
// per-patch cadence, so the container renders hardcoded data for launch
// (see components/TierList/pages/Classic). This hook is the switch-over
// point: once the pipeline is live, point the container at it. URL mirrors
// the attached sample:
//   https://static.bigbrain.gg/custom-league-classic/<patch>/tierlist-champions-<patch>-v1.json
// with `patch` resolved via the CLASSIC_TIER_LIST page params (the patch
// filter swaps the version segment).
export function useClassicChampionTierList<K = ReturnType<typeof cleanClassicChampionTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<ClassicChampionTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.CLASSIC_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const effectiveSkip = skip || !patch;

  const url = `https://static.bigbrain.gg/custom-league-classic/${patch}/tierlist-champions-${patch}-v1.json`;
  return useJSONFetcher<ClassicChampionTierList, K>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: onCompleted ?? ((url, json) => cleanClassicChampionTierList(json) as K),
    customCacheKey,
  });
}
